/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik, FormikProvider, FieldArray} from 'formik'
import {KTSVG} from '../../../helpers'

import {KTCard, KTCardBody, Select, Loading, Datepicker} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {warehouseLookup, allProductLookup, projectLookup} from '../../../redux/slices/lookup.slice'
import {warehouseTransferCreate} from '../../../redux/slices/product.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  warehouseLookupList: state.lookup.warehouseLookupList,
  productLookup: state.lookup.productLookup,
  projectLookupList: state.lookup.projectLookupList,
  loading: state.lookup.loading,
})

const selectProductData = (state) => ({
  isRedirect: state.product.isRedirect,
  loading: state.product.loading,
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {warehouseLookupList, productLookup, projectLookupList, loading} = useSelector(
    selectLookupData
  )
  const {isRedirect, loading: createLoading} = useSelector(selectProductData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(warehouseLookup())
    dispatch(allProductLookup())
    dispatch(projectLookup())
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(
    () =>
      Yup.object().shape({
        ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        DepartureWarehouseId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        EntryWarehouseId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        IssueDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        ShipmentDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      }),
    [intl]
  )

  const formValues = useMemoizedFormValues({
    Name: '',
    ProjectId: null,
    DepartureWarehouseId: '',
    EntryWarehouseId: '',
    DeliveryPerson: '',
    ReceiverPerson: '',
    IssueDate: new Date(),
    ShipmentDate: new Date(),
    WarehouseTransferItems: [
      {
        ProductId: '',
        Amount: '',
      },
    ],
  })

  const onClickNewItem = useCallback((arrayHelpers) => {
    arrayHelpers.push({
      ProductId: '',
      Amount: '',
    })
  }, [])

  const handleSubmit = useCallback(
    async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      dispatch(warehouseTransferCreate(values))
      resetForm()
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.WAREHOUSE_TRANSFER'}),
        path: '/stock/warehouse-transfer',
        isSeparator: false,
        isActive: false,
      },
      {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
      },
    ],
    [intl]
  )

  useEffect(() => {
    if (isRedirect && isRedirect.id) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
  }, [isRedirect, navigate, pageUrl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.WAREHOUSE_TRANSFER.ADD'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          <FormikProvider value={formik}>
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'WAREHOUSETRANSFER.CREATE.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'WAREHOUSETRANSFER.CREATE.PROJECT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={projectLookupList}
                    required
                    {...formik.getFieldProps('ProjectId')}
                    onChange={(item) => formik.setFieldValue('ProjectId', Number(item?.value))}
                  />
                  {formik.touched.ProjectId && formik.errors.ProjectId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ProjectId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'WAREHOUSETRANSFER.CREATE.DEPARTUREWAREHOUSE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={warehouseLookupList}
                    required
                    {...formik.getFieldProps('DepartureWarehouseId')}
                    onChange={(item) =>
                      formik.setFieldValue('DepartureWarehouseId', Number(item?.value))
                    }
                  />
                  {formik.touched.DepartureWarehouseId && formik.errors.DepartureWarehouseId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.DepartureWarehouseId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'WAREHOUSETRANSFER.CREATE.DELIVERYPERSON'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('DeliveryPerson')}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'WAREHOUSETRANSFER.CREATE.ENTRYWAREHOUSE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={warehouseLookupList}
                    required
                    {...formik.getFieldProps('EntryWarehouseId')}
                    onChange={(item) =>
                      formik.setFieldValue('EntryWarehouseId', Number(item?.value))
                    }
                  />
                  {formik.touched.EntryWarehouseId && formik.errors.EntryWarehouseId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.EntryWarehouseId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'WAREHOUSETRANSFER.CREATE.RECEIVERPERSON'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('ReceiverPerson')}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'WAREHOUSETRANSFER.CREATE.ISSUEDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('IssueDate')}
                    onChange={([date]) => formik.setFieldValue('IssueDate', date)}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'WAREHOUSETRANSFER.CREATE.SHIPMENTDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('ShipmentDate')}
                    onChange={([date]) => formik.setFieldValue('ShipmentDate', date)}
                  />
                </div>
              </div>
              <FieldArray
                name='WarehouseTransferItems'
                render={(arrayHelpers) => (
                  <>
                    <div className='table-responsive'>
                      <table className='table g-5 gs-0 mb-0 fw-bold text-gray-700'>
                        <thead>
                          <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                            <th>
                              {intl.formatMessage({
                                id: 'WAREHOUSETRANSFER.CREATE.WAREHOUSETRANSFERITEMS.PRODUCTID',
                              })}
                            </th>
                            <th>
                              {intl.formatMessage({
                                id: 'WAREHOUSETRANSFER.CREATE.WAREHOUSETRANSFERITEMS.AMOUNT',
                              })}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {formik.values.WarehouseTransferItems.map((item, index) => (
                            <tr className='border-bottom border-bottom-dashed' key={index}>
                              <td className='ps-1'>
                                <Select
                                  data={productLookup}
                                  name={`WarehouseTransferItems[${index}].ProductId`}
                                  value={formik.values.WarehouseTransferItems[index].ProductId}
                                  required
                                  onChange={(item) =>
                                    formik.setFieldValue(
                                      `WarehouseTransferItems[${index}].ProductId`,
                                      Number(item?.value)
                                    )
                                  }
                                />
                              </td>
                              <td className='ps-0'>
                                <input
                                  type='number'
                                  className='form-control form-control-solid'
                                  autoComplete='off'
                                  disabled={formik.isSubmitting}
                                  placeholder={intl.formatMessage({
                                    id: 'WAREHOUSETRANSFER.CREATE.WAREHOUSETRANSFERITEMS.AMOUNT',
                                  })}
                                  name={`WarehouseTransferItems[${index}].Amount`}
                                  value={formik.values.WarehouseTransferItems[index].Amount}
                                  onChange={(event) => formik.handleChange(event)}
                                  onFocus={(event) => event.target.select()}
                                />
                              </td>

                              <td className='pt-5 text-end'>
                                <div className='d-flex'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-icon btn-active-color-primary'
                                    onClick={() => arrayHelpers.remove(index)}
                                  >
                                    <KTSVG
                                      path='/images/icons/duotune/general/gen027.svg'
                                      className='svg-icon-2'
                                    />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      type='button'
                      className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                      onClick={() => onClickNewItem(arrayHelpers)}
                    >
                      <KTSVG
                        path='/images/icons/duotune/arrows/arr009.svg'
                        className='svg-icon-2'
                      />
                      {intl.formatMessage({id: 'ADD_NEW_LINE'})}
                    </button>
                  </>
                )}
              />
              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </FormikProvider>
        </KTCardBody>
      </KTCard>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
